import type { Host, InstallInfo, NodeInfo } from '../../api'
import { t } from '../../i18n'
import { releaseVersion } from './nodeSwitch'

/*
 * Planned updates of a node (node-holders §8.3, HOLD-A8). Logic and the agent change
 * with the broadcasts on air (the host agent's blue-green); the holders change only on
 * a freed node — a deploy of the release. The console never paints the node DRAINING
 * or invents an UPDATING chip.
 */

/** Wrong codes in a row before the center locks confirmations (provisioning.ts). */
export const CODE_TRIES = 5

export interface LogicPair {
  current: string
  next?: string
}

/**
 * Logic now and, when the center's release changes it on this host, the next one.
 * The host's components come from its agent (heartbeat); without them, the node's
 * version or the host's release number.
 */
export function logicPair(n: NodeInfo, host: Host | null | undefined, info?: InstallInfo, empty = '—'): LogicPair {
  const current = host?.components?.logic?.version || n.version || (host?.release_no !== undefined ? `#${host.release_no}` : '') || empty
  let next: string | undefined
  if (host?.changes?.includes('logic')) next = releaseVersion(info, 'logic')
  else if (!host?.changes && info?.release_no !== undefined && host?.release_no !== undefined && info.release_no > host.release_no) {
    next = info.image_tag || `#${info.release_no}`
  }
  if (next === current) next = undefined
  return { current, next }
}

/** Publishers the node still has. The drain report wins when the node sent one; otherwise the placed streams. */
export function publishersOn(n: NodeInfo): number {
  if (n.drain?.reported && n.drain.publishers !== undefined) return n.drain.publishers
  return n.streams ?? 0
}

export function hostBlock(n: NodeInfo, host: Host | null | undefined, hostsOn: boolean): string {
  if (n.status === 'revoked') return 'This node is revoked.'
  if (!hostsOn) return 'This center has no host agent.'
  if (!host) return 'No host agent on this node.'
  if (host.installing) return 'An install is running on this host.'
  if (!host.online) return 'The host agent is offline.'
  return ''
}

/**
 * Why Update holders is off. Holders move only on a drained node with no publisher:
 * a node that still has streams is freed first (move the streams).
 */
export function holdersBlock(n: NodeInfo, host: Host | null | undefined, hostsOn: boolean): string {
  if (publishersOn(n) > 0) return t('node.why.free')
  if (n.drain?.state === 'draining') return 'This node is still draining.'
  if (n.drain?.state !== 'drained') return 'This node is not drained.'
  return hostBlock(n, host, hostsOn)
}

export const emptyCode = (): string[] => ['', '', '', '', '', '']

export const codeOf = (cells: string[]) => cells.join('')

export const codeReady = (cells: string[]) => /^\d{6}$/.test(codeOf(cells))
